import { useState } from 'react'
import { config } from 'data/config'
import LayeredInformationSection from 'components/sections/LayeredInformationSection'
import InformationSection from 'components/sections/InformationSection'
import ImageHeader from 'components/ImageHeader'
import PartnerSearchbar from 'components/searchbar/PartnerSearchbar'
import BannerCardSection from 'components/sections/BannerCardSection'
import PartnerInquiryModal from 'components/PartnerInquiryModal'
import Hero from 'assets/images/partners/list-your-property-hero.png'
import HeaderImage from 'assets/images/partners/list-your-property-header.png'
import ImageOne from 'assets/images/partners/list-your-property-1.png'
import ImageTwo from 'assets/images/partners/list-your-property-2.png'
import BannerImage from 'assets/images/partners/list-your-property-banner.png'
import PublicLayout, { PublicLayoutProps } from 'layouts/PublicLayout'
import { useHasScrolled } from 'data/hooks/useHasScrolled'
import { useMobileApp } from 'data/hooks/MobileApp/useMobileApp'
import { InfoCircleThinIcon } from 'components/icons'

const OwnersPage = () => {
  const [formModalOpen, setFormModalOpen] = useState(false)
  const { isMobileApp } = useMobileApp()
  const hasScrolled = useHasScrolled(0, true)

  const seo: PublicLayoutProps['seo'] = {
    title: 'A.M.A Selections for Luxury Holiday Villa Rentals in Spain, France & Italy',
    description: 'Contact A.M.A Selections at any time to find out more about availability & exact rates of our luxury holiday villas for rent in France, Spain & Italy.',
    openGraph: {
      type: 'business',
      url: config.appURL + '/owners',
    },
  }

  return (
    <PublicLayout
      seo={seo}
      mobileNavBar={{
        visible: isMobileApp || hasScrolled,
        back: true,
        profileTrigger: true,
        links: [
          {
            label: 'Owners',
            href: '/owners',
            active: true,
          },
          {
            label: 'Travel Partners',
            href: '/travel-partners',
          },
        ],
      }}
    >
      <PartnerSearchbar currentPage="travel-partners" />

      <ImageHeader
        title="List Your Property with A.M.A Selections"
        image={Hero}
        altText="A serene oasis awaits, with a crystal-clear pool embraced by elegant lawn chairs and umbrellas."
      />

      <InformationSection
        subtitle="Who we are"
        title="The world’s Premier Luxury Vacation Rental Company"
        buttonText="Apply now"
        buttonAction={() => setFormModalOpen(true)}
        text={[
          'A.M.A Selections is a leading booking platform offering a curated selection of the world’s most exceptional holiday homes. We offer over 1500 professionally managed properties, each handpicked by our team for their character, amenities and comfort.',
          'We understand the ever-evolving needs of our guests and value the homes that lend to exceptional stays. We are dedicated to working with the very best property owners and local home management companies that align with our meticulous quality standards and commitment to service. If you’d like to join A.M.A Selections as a property owner, property manager or local rental agency, we would be delighted to learn more. Apply today to list your property with us.',
        ]}
        image={{
          url: ImageOne,
          altText: 'Luxurious living room with plush couch, elegant coffee table, and captivating wall art.',
        }}
      />

      <LayeredInformationSection
        header="Join The world’s Best"
        image={{
          url: ImageTwo,
          alt: 'Elegant bed adorned with plump pillows, accompanied by a graceful lamp',
        }}
        imageAlignment="right"
        content={[
          {
            header: 'Top 1% of Properties',
            content: 'Joining A.M.A Selections grants you access to a prestigious network of discerning guests and luxury travel advisors. Your home is in good company—our curated portfolio boasts some of the most extraordinary properties in the world. Our dedicated team will create the listing on your behalf and work diligently to make your property stand out, ensuring a greater return.',
          },
          {
            header: 'Global Reach',
            content: 'There are no fees to list your home; we only earn a commission when you receive a booking, guaranteeing that our performance remains front-of-mind. Our marketing team generates quality leads through highly targeted online search, display, and social media advertising, international press, and newsletter outreach. Furthermore, as official partners of Marriott International, you can opt in to showcase your listing to their 150 million+ loyalty members.',
          },
          {
            header: 'Trusted Guests',
            content: 'Most of our guests are traveling with family, seeking the space and privacy that a home provides to create memories with their loved ones. Our guests, often returning, are discerning travelers who appreciate and respect the homes we offer. For security purposes, our comprehensive identity verification substantiates guest details, including name, address, and government ID. Additionally, we never accept bookings that show a high risk for disruptive parties and property damage.',
          },
        ]}
      />

      <LayeredInformationSection
        header="Are You a Local Agency?"
        image={{
          url: HeaderImage,
          alt: 'A lavish coffee table adorned with a captivating book',
        }}
        imageAlignment="left"
        content={[
          {
            header: 'Let’s Grow Together',
            content: 'We value your local expertise and strive to be a partner you can depend on. Understanding the level of experience and time required to manage local operations, we’re here to enable you to focus on what you do best. We handle marketing and the relationship with our client, while you manage the operations and relationship with the property owner. Together, we can grow and establish greater market share.',
          },
          {
            header: 'Manage Bookings with Ease',
            content: 'The relationship with our partners is paramount to our success. We believe that the fundamentals of a great partnership begin with trust and transparency. We’re here to protect your reputation and valuable relationship with your property owners, and we’re entrusting our guests’ stay to you. By joining A.M.A Selections, you’ll enjoy the benefits of an efficient and collaborative partnership.',
          },
        ]}
      />

      <BannerCardSection
        includePadding={false}
        heading="Complete application to list with us"
        subtitle="Join A.M.A Selections"
        content="Are you the owner or manager of an exceptional property with a professional on-site team that you believe would be a good fit for our portfolio? Apply today to list your property with us."
        buttons={[
          {
            text: 'Apply now',
            action: () => setFormModalOpen(true),
            icon: <InfoCircleThinIcon />,
          },
        ]}
        bannerImage={BannerImage}
        altText="Mountain view from a cozy chalet living room."
      />
      <div className="h-50 lg:h-0" />

      <PartnerInquiryModal isOpen={formModalOpen} onClose={() => setFormModalOpen(false)} />
    </PublicLayout>
  )
}

export default OwnersPage
